/**
 * DocsSearch — fuzzy search across the docs manifest.
 *
 * Requirement 3 (`.kiro/specs/help-docs-panel/requirements.md`):
 *  - 3.1 Fuzzy match against title / heading text / path.
 *  - 3.2 Debounce input by 150 ms.
 *  - 3.3 Cap results at 8, expose `role="listbox"` with `role="option"` items.
 *  - 3.4 Enter picks the highlighted match; when the hit is on a heading,
 *        the heading `slug` is passed as the second `onPick` argument.
 *  - 3.5 Escape clears the input but keeps the slider open.
 *
 * Design § Component 4:
 *  - Fuse.js index built once via `useMemo` keyed on `manifest.generatedAt`.
 *  - Weights: title 0.6, path 0.2, headings.text 0.2; threshold 0.4;
 *    `includeMatches: false`, `includeScore: false`.
 *
 * Leaf component per plan Wave 2.4 — imports only from `./types`.
 *
 * @packageDocumentation
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import Fuse from "fuse.js";
import type {
  DocsManifest,
  DocsManifestEntry,
  DocsManifestHeading,
} from "./types";

/**
 * Props for `DocsSearch`.
 */
export interface DocsSearchProps {
  /** The manifest loaded by the caller. */
  manifest: DocsManifest;
  /**
   * Called when the user picks a result.
   *
   * @param path - the entry `path` from the manifest.
   * @param anchor - heading slug when the hit is on a heading, else `undefined`.
   */
  onPick(path: string, anchor?: string): void;
}

interface SearchResult {
  entry: DocsManifestEntry;
  anchor?: string;
  headingText?: string;
}

const MAX_RESULTS = 8;
const DEBOUNCE_MS = 150;
const LISTBOX_ID = "docs-search-listbox";
const OPTION_ID_PREFIX = "docs-search-option-";

export function DocsSearch(props: DocsSearchProps): JSX.Element {
  const { manifest, onPick } = props;

  const [query, setQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Primary Fuse index over the whole manifest.
  // Rebuilt only when the manifest's generatedAt timestamp changes (design § 4).
  const fuse = useMemo<Fuse<DocsManifestEntry>>(
    () =>
      new Fuse(manifest.tree, {
        keys: [
          { name: "title", weight: 0.6 },
          { name: "path", weight: 0.2 },
          { name: "headings.text", weight: 0.2 },
        ],
        threshold: 0.4,
        includeMatches: false,
        includeScore: false,
      }),
    // Intentional: rebuild only when the manifest generation timestamp
    // changes. Passing `manifest.tree` directly would rebuild on every
    // parent re-render because callers may hand us fresh arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manifest.generatedAt],
  );

  // Per-entry heading Fuses — used to derive the anchor when a hit
  // is on a heading (design § Component 4 behaviour #4).
  const headingFuses = useMemo<Map<string, Fuse<DocsManifestHeading>>>(() => {
    const map = new Map<string, Fuse<DocsManifestHeading>>();
    for (const entry of manifest.tree) {
      if (entry.headings.length > 0) {
        map.set(
          entry.path,
          new Fuse(entry.headings, {
            keys: ["text"],
            threshold: 0.4,
            includeMatches: false,
            includeScore: false,
          }),
        );
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest.generatedAt]);

  // Debounce input by 150 ms (Req 3.2). setTimeout + cleanup ref pattern.
  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
      timerRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [query]);

  // Compute results whenever the debounced query settles.
  const results = useMemo<SearchResult[]>(() => {
    const trimmed = debouncedQuery.trim();
    if (trimmed === "") return [];
    const primary = fuse.search(trimmed).slice(0, MAX_RESULTS);
    return primary.map(({ item }) => {
      const headingFuse = headingFuses.get(item.path);
      if (headingFuse !== undefined) {
        const headingHits = headingFuse.search(trimmed);
        if (headingHits.length > 0) {
          const first = headingHits[0].item;
          return { entry: item, anchor: first.slug, headingText: first.text };
        }
      }
      return { entry: item };
    });
  }, [debouncedQuery, fuse, headingFuses]);

  // Reset highlight whenever the result set shifts.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [results]);

  const pickAt = useCallback(
    (index: number): void => {
      const chosen = results[index];
      if (chosen === undefined) return;
      onPick(chosen.entry.path, chosen.anchor);
    },
    [results, onPick],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((i) =>
          results.length === 0 ? 0 : Math.min(i + 1, results.length - 1),
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === "Enter") {
        event.preventDefault();
        pickAt(highlightedIndex);
      } else if (event.key === "Escape") {
        // Req 3.5 — clear input; the slider itself is owned by the parent
        // and MUST stay open. We flush both the raw and debounced query
        // synchronously so no stale timer re-populates the listbox.
        event.preventDefault();
        setQuery("");
        setDebouncedQuery("");
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    },
    [results.length, highlightedIndex, pickAt],
  );

  const showListbox = results.length > 0;

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-label="Search documentation"
        aria-controls={showListbox ? LISTBOX_ID : undefined}
        aria-expanded={showListbox}
        aria-autocomplete="list"
        aria-activedescendant={
          showListbox ? `${OPTION_ID_PREFIX}${highlightedIndex}` : undefined
        }
        autoComplete="off"
        placeholder="Search documentation…"
        className="w-full h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {showListbox && (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Documentation search results"
          className="absolute z-10 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg"
        >
          {results.map((result, index) => {
            const selected = index === highlightedIndex;
            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/mouse-events-have-key-events
              <li
                key={result.entry.path}
                id={`${OPTION_ID_PREFIX}${index}`}
                role="option"
                aria-selected={selected}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-100${
                  selected ? " bg-primary-100 text-primary-800" : ""
                }`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  // Prevent the mousedown from stealing focus from the
                  // input before onClick fires.
                  e.preventDefault();
                }}
                onClick={() => pickAt(index)}
              >
                <div className="text-sm font-medium">{result.entry.title}</div>
                <div className="text-xs text-gray-500">{result.entry.path}</div>
                {result.headingText !== undefined && (
                  <div className="text-xs text-gray-500">
                    → {result.headingText}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
